import * as React from "react"

import { cn } from "../../cn"
import { useCopyToClipboardFeedback } from "../../hooks/useCopyToClipboardFeedback"
import { Tooltip } from "../Tooltip/Tooltip"

export interface CopyableIdProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value"
> {
  /** The full identifier. Copied to the clipboard in full on click. */
  id: string
  /** Number of leading characters to display. Defaults to 4. */
  chars?: number
  /** Accessible label and hover tooltip text. Defaults to "Copy ID". */
  label?: string
  /** Tooltip text shown after a successful copy. Defaults to "Copied!". */
  copiedLabel?: string
}

/**
 * Standardized table ID cell. Displays the first `chars` characters of an id
 * (4 by default, no dots or trailing characters). Hover brightens the characters
 * and shows the copy hint; clicking copies the full id to the clipboard and
 * holds a confirmation tooltip that clears itself.
 */
const CopyableId = React.forwardRef<HTMLButtonElement, CopyableIdProps>(
  (
    {
      id,
      chars = 4,
      label = "Copy ID",
      copiedLabel = "Copied!",
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { copied, copy } = useCopyToClipboardFeedback()

    const short = id.slice(0, chars)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onClick?.(event)
      void copy(id)
    }

    return (
      <Tooltip content={copied ? copiedLabel : label} forceOpen={copied}>
        <button
          type="button"
          ref={ref}
          onClick={handleClick}
          aria-label={label}
          className={cn(
            "inline-flex items-center justify-center",
            "font-mono text-sm text-coval-text-muted transition-colors hover:text-coval-text",
            "rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border",
            className,
          )}
          {...props}
        >
          {short}
        </button>
      </Tooltip>
    )
  },
)

CopyableId.displayName = "CopyableId"

export { CopyableId }
